import React, { useEffect, useState } from "react";
import { fetchData } from "../../utils/functions";
import { toast } from "react-toastify";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  ThinDropdownWithSearch,
  ThinTextField,
} from "../common/GeneralComponent";

const EmploymentPanel = ({
  household_listings = [],
  setEmploymentData,
  DqaApproved,
  DqaRejected
}) => {
  const isDqaApproved = DqaApproved || false;
  const isDqaRejected = DqaRejected || false

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();

  const [districts, setDistricts] = useState([]);
  const [currentEmployments, setCurrentEmployments] = useState([]);
  const [errors, setErrors] = useState({});
  const [hasEligibleMembers, setHasEligibleMembers] = useState(true);

  const employmentData = household_listings
    .filter((member) => member.age >= 10)
    .map((member) => ({
      household_employment_id:
        member.HouseholdEmployeement?.household_employment_id,
      household_listing_id: member.household_listing_id,
      worked_last_month: member.HouseholdEmployeement?.worked_last_month,
      occupation_name: member.HouseholdEmployeement?.occupation_name,
      days_worked_last_month:
        member.HouseholdEmployeement?.days_worked_last_month,
      earnings_last_month: member.HouseholdEmployeement?.earnings_last_month,
      work_location_district_id:
        member.HouseholdEmployeement?.work_location_district_id,
      household_name: member.household_name,
      household_age: member.age,
      occupation_others_specify:
        member.HouseholdEmployeement?.occupation_others_specify,
      HouseholdListing: {
        household_name: member.household_name,
        age: member.age,
      },
    }));

  // Helpers
  const toBool = (v) =>
    v === true || v === 1 || v === "1"
      ? true
      : v === false || v === 0 || v === "0"
      ? false
      : undefined;

  const getOptValue = (opt) =>
    opt && typeof opt === "object" && !Array.isArray(opt)
      ? opt.value ?? ""
      : opt;

  const isEmpty = (v) =>
    v === null || v === undefined || String(v).trim() === "";
  const req = (v) => !isEmpty(v);

  useEffect(() => {
    if (Array.isArray(employmentData) && employmentData.length > 0) {
      const eligibleMembers = employmentData.filter((item) => {
        const age = item.household_age || 0;
        return age >= 10;
      });

      if (eligibleMembers.length === 0) {
        setHasEligibleMembers(false);
        setCurrentEmployments([]);
        return;
      }

      setHasEligibleMembers(true);
      const normalized = eligibleMembers.map((r) => ({
        ...r,
        worked_last_month: toBool(r.worked_last_month),
        occupation_name: getOptValue(r.occupation_name) || null,
        days_worked_last_month: r.days_worked_last_month ?? null,
        earnings_last_month: r.earnings_last_month ?? null,
        work_location_district_id: r.work_location_district_id ?? null,
        occupation_others_specify: r.occupation_others_specify ?? "",
      }));
      setCurrentEmployments(normalized);
    } else {
      setHasEligibleMembers(false);
      setCurrentEmployments([]);
    }
  }, [household_listings]);

  useEffect(() => {
    const fetchDistricts = async () => {
      try {
        const response = await fetchData("districts", "GET");
        setDistricts(response?.data || []);
      } catch (error) {
        console.error("Error fetching districts:", error);
      }
    };
    fetchDistricts();
  }, []);

  const districtOptions = districts.map((d) => ({
    value: d.district_id,
    label: d.district_name,
  }));

  const getOccupationOptions = (gender) => {
    const baseOccupations = [
      { value: "Government service", label: "Government service" },
      { value: "Doctor", label: "Doctor" },
      { value: "Engineer", label: "Engineer" },
      { value: "Teacher", label: "Teacher" },
      { value: "Compounder", label: "Compounder" },
      { value: "Peon", label: "Peon" },
      { value: "Mason", label: "Mason" },
      { value: "Tailor", label: "Tailor" },
      { value: "Labourer", label: "Labourer" },
      { value: "Shopkeeper", label: "Shopkeeper" },
      { value: "Carpenter", label: "Carpenter" },
      { value: "Metal work", label: "Metal work" },
      { value: "Plumber", label: "Plumber" },
      { value: "Electrician", label: "Electrician" },
      { value: "Mechanic", label: "Mechanic" },
      { value: "Farmer/agriculture work", label: "Farmer/Agriculture work" },
      { value: "Cook", label: "Cook" },
      { value: "Mobile repair", label: "Mobile repair" },
      { value: "Handicraft", label: "Handicraft" },
      { value: "Beautician", label: "Beautician" },
      { value: "Barber", label: "Barber" },
      { value: "Security Guard", label: "Security Guard" },
      { value: "Others", label: "Others" },
      { value: "Embroidery", label: "Embroidery" },
      { value: "Nurse", label: "Nurse" },
      { value: "Working abroad", label: "Working abroad" },
    ];

    // Female-specific occupations
    const femaleSpecificOccupations = [
      { value: "Midwife", label: "Midwife" },
      { value: "LHW", label: "LHW" },
      { value: "LHV", label: "LHV" },
    ];

    // If gender is female, include all occupations
    if (gender === "Female") {
      return [...baseOccupations, ...femaleSpecificOccupations];
    }

    // For male or any other gender, return only base occupations (excluding LHW, LHV, Midwife)
    return baseOccupations;
  };

  const setFieldError = (idx, field, msg) =>
    setErrors((prev) => ({
      ...prev,
      [idx]: { ...(prev[idx] || {}), [field]: msg },
    }));

  const clearFieldError = (idx, field) =>
    setErrors((prev) => {
      const next = { ...(prev[idx] || {}) };
      delete next[field];
      return { ...prev, [idx]: next };
    });

  const handleChange = (index, field, value) => {
    setCurrentEmployments((prev) => {
      const updated = [...prev];
      let newVal = value;

      if (field === "worked_last_month") {
        newVal = toBool(value);
        if (newVal === false) {
          updated[index] = {
            ...updated[index],
            worked_last_month: false,
            days_worked_last_month: null,
            occupation_name: null,
            occupation_others_specify: null, // Clear if not worked
            earnings_last_month: null,
            work_location_district_id: null,
          };
          clearFieldError(index, "days_worked_last_month");
          clearFieldError(index, "occupation_name");
          clearFieldError(index, "earnings_last_month");
          clearFieldError(index, "work_location_district_id");
        } else {
          updated[index] = { ...updated[index], worked_last_month: true };
        }
      } else if (field === "occupation_name") {
        newVal = getOptValue(value);
        if (newVal === "" || newVal === undefined) {
          newVal = null;
        }
        updated[index] = { ...updated[index], [field]: newVal };

        // Clear occupation_others_specify if occupation_name is not "Others"
        if (newVal !== "Others") {
          updated[index] = { ...updated[index], occupation_others_specify: "" };
        }
      } else {
        updated[index] = { ...updated[index], [field]: newVal };
      }
      return updated;
    });
    clearFieldError(index, field);
  };

  const validateRecord = (emp, idx) => {
    let ok = true;

    const worked = toBool(emp.worked_last_month);
    if (worked === undefined) {
      setFieldError(idx, "worked_last_month", "Please select Yes or No.");
      ok = false;
    }

    if (worked === true) {
      if (!req(emp.days_worked_last_month)) {
        setFieldError(
          idx,
          "days_worked_last_month",
          "Days worked last month is required."
        );
        ok = false;
      } else {
        const n = Number(emp.days_worked_last_month);
        if (!Number.isInteger(n) || n < 1 || n > 31) {
          setFieldError(
            idx,
            "days_worked_last_month",
            "Enter a whole number between 1 and 31."
          );
          ok = false;
        }
      }

      const occ = getOptValue(emp.occupation_name);
      if (isEmpty(occ) || occ === null) {
        setFieldError(idx, "occupation_name", "Nature of Work is Required.");
        ok = false;
      }

      if (
        emp.occupation_name === "Others" &&
        isEmpty(emp.occupation_others_specify)
      ) {
        setFieldError(
          idx,
          "occupation_others_specify",
          "Nature of work specify is required."
        );
        ok = false;
      }

      if (!req(emp.earnings_last_month)) {
        setFieldError(
          idx,
          "earnings_last_month",
          "Last month earnings is required."
        );
        ok = false;
      } else if (
        isNaN(Number(emp.earnings_last_month)) ||
        Number(emp.earnings_last_month) < 0
      ) {
        setFieldError(
          idx,
          "earnings_last_month",
          "Enter a non-negative number."
        );
        ok = false;
      }

      if (isEmpty(emp.work_location_district_id)) {
        setFieldError(
          idx,
          "work_location_district_id",
          "District is required."
        );
        ok = false;
      }
    }

    return ok;
  };

  const validateAll = () => {
    setErrors({});
    let allOk = true;
    currentEmployments.forEach((emp, idx) => {
      const ok = validateRecord(emp, idx);
      if (!ok) allOk = false;
    });
    return allOk;
  };

  const handleUpdateEmployment = async (e) => {
    e.preventDefault();

    if (currentEmployments.length === 0) {
      return toast.error("No employment data to update");
    }

    if (!validateAll()) {
      return;
    }

    try {
      const results = await Promise.all(
        currentEmployments.map(async (employment) => {
          const occupationValue = getOptValue(employment.occupation_name);
          const workedLastMonth = toBool(employment.worked_last_month);

          const toIntOrNull = (value) => {
            if (value === "" || value === null || value === undefined) {
              return null;
            }
            const num = Number(value);
            return isNaN(num) ? null : num;
          };

          let payload = {
            household_employment_id: employment.household_employment_id,
            household_listing_id: employment.household_listing_id,
            worked_last_month: workedLastMonth ? 1 : 0,
            household_information_id: id,
          };

          if (workedLastMonth) {
            payload = {
              ...payload,
              occupation_name:
                occupationValue && occupationValue.trim() !== ""
                  ? occupationValue
                  : null,
              occupation_others_specify:
                employment.occupation_others_specify &&
                employment.occupation_others_specify.trim() !== ""
                  ? employment.occupation_others_specify
                  : null,
              days_worked_last_month: toIntOrNull(
                employment.days_worked_last_month
              ),
              earnings_last_month:
                employment.earnings_last_month &&
                employment.earnings_last_month.trim() !== ""
                  ? employment.earnings_last_month
                  : null,
              work_location_district_id: toIntOrNull(
                employment.work_location_district_id
              ),
            };
          } else {
            payload = {
              ...payload,
              occupation_name: null,
              occupation_others_specify: null, // Reset field when not working
              days_worked_last_month: null,
              earnings_last_month: null,
              work_location_district_id: null,
            };
          }

          const endpoint = employment.household_employment_id
            ? `householdemployment-update/${employment.household_employment_id}`
            : `householdemployment-create`;

          const method = employment.household_employment_id ? "PUT" : "POST";

          return await fetchData(endpoint, method, payload);
        })
      );

      const allSuccess = results.every((res) => res?.success);
      if (allSuccess) {
        toast.success("All employment data updated successfully");
        if (setEmploymentData) {
          setEmploymentData((prev) => {
            const updated = prev ? [...prev] : [];
            results.forEach((res, index) => {
              const existingIndex = updated.findIndex(
                (item) =>
                  item.household_employment_id ===
                  res.data.household_employment_id
              );
              if (existingIndex >= 0) {
                updated[existingIndex] = {
                  ...res.data,
                  "houselisting.household_name":
                    currentEmployments[index]?.household_name,
                  "houselisting.age": currentEmployments[index]?.household_age,
                  household_name: currentEmployments[index]?.household_name,
                  age: currentEmployments[index]?.household_age,
                };
              } else {
                updated.push({
                  ...res.data,
                  "houselisting.household_name":
                    currentEmployments[index]?.household_name,
                  "houselisting.age": currentEmployments[index]?.household_age,
                  household_name: currentEmployments[index]?.household_name,
                  age: currentEmployments[index]?.household_age,
                });
              }
            });
            return updated;
          });
        }
      } else {
        toast.error("Some employment data failed to save");
      }
    } catch (error) {
      toast.error("An error occurred while saving employment data");
      console.error(error);
    }
  };

  const Err = ({ index, name }) =>
    errors?.[index]?.[name] ? (
      <p className="text-[0.9rem] text-red-600 mt-2">{errors[index][name]}</p>
    ) : null;

  if (!hasEligibleMembers) {
    return (
      <div>
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            {isDqaApproved && (
              <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
                Approved from DQA
              </span>
            )}
          </div>
        </div>

        <div className="text-center py-8">
          <p className="text-gray-500 italic text-lg">
            No household members aged 10 or above
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          {isDqaApproved && (
            <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full text-sm font-semibold">
              Approved from DQA
            </span>
          )}
        </div>
      </div> */}

      <div className="flex justify-between items-center mb-3">
        {isDqaApproved && (
          <>
            <h2 className="text-teal-700 text-xl font-semibold">
              Household Employment And Occupation
            </h2>
            <span className="bg-blue-100 text-blue-800 px-1 py-1 rounded-full text-sm font-semibold">
              Approved from DQA
            </span>
          </>
        )}
        {isDqaRejected && (
          <>
            <h2 className="text-teal-700 text-xl font-semibold">
              Household Employment And Occupation
            </h2>

            <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-semibold">
              Rejected from DQA
            </span>
          </>
        )}
      </div>
      {currentEmployments.map((employment, index) => {
        const empId = employment.household_employment_id || index;
        const workedLastMonth = toBool(employment.worked_last_month);

        return (
          <div
            key={empId}
            className="mb-4 p-4 border border-gray-200 rounded-lg"
          >
            {/* Member name and age */}
            <div className="mb-2">
              <h3 className="text-base font-medium text-gray-800">
                {employment.household_name || `Member ${index + 1}`}
                {employment.household_age && (
                  <span className="text-sm text-gray-600 ml-2">
                    (Age: {employment.household_age})
                  </span>
                )}
              </h3>
            </div>

            {/* Worked Last Month - Always visible */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div>
                <p className="mb-1 text-sm font-medium text-gray-700">
                  Worked Last Month
                </p>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-1">
                    <input
                      type="radio"
                      name={`worked_last_month_${index}`}
                      checked={workedLastMonth === true}
                      onChange={() =>
                        handleChange(index, "worked_last_month", true)
                      }
                      disabled={isDqaApproved || isDqaRejected}
                    />{" "}
                    Yes
                  </label>
                  <label className="flex items-center gap-1">
                    <input
                      type="radio"
                      name={`worked_last_month_${index}`}
                      checked={workedLastMonth === false}
                      onChange={() =>
                        handleChange(index, "worked_last_month", false)
                      }
                      disabled={isDqaApproved || isDqaRejected}
                    />{" "}
                    No
                  </label>
                </div>
                <Err index={index} name="worked_last_month" />
              </div>
            </div>

            {/* Employment fields - Only show if worked_last_month is true */}
            {workedLastMonth === true && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                  {/* Days Worked Last Month */}
                  <div>
                    <ThinTextField
                      label="Days Worked Last Month"
                      type="number"
                      value={employment.days_worked_last_month || ""}
                      onChange={(e) =>
                        handleChange(
                          index,
                          "days_worked_last_month",
                          e.target.value || null,
                        )
                      }
                      disabled={isDqaApproved || isDqaRejected}
                    />
                    <Err index={index} name="days_worked_last_month" />
                  </div>

                  {/* Nature of Work */}
                  <div>
                    <ThinDropdownWithSearch
                      label="Nature of Work"
                      options={getOccupationOptions(
                        employmentData.find(
                          (emp) =>
                            emp.household_listing_id ===
                            employment.household_listing_id,
                        )?.HouseholdListing?.gender ||
                          household_listings.find(
                            (member) =>
                              member.household_listing_id ===
                              employment.household_listing_id,
                          )?.gender,
                      )}
                      value={employment.occupation_name || ""}
                      onChange={(val) =>
                        handleChange(index, "occupation_name", getOptValue(val))
                      }
                      disabled={isDqaApproved || isDqaRejected}
                    />
                    <Err index={index} name="occupation_name" />
                  </div>

                  {/* Work Location District */}
                  <div>
                    <ThinDropdownWithSearch
                      label="District of Work"
                      options={districtOptions}
                      value={employment.work_location_district_id || ""}
                      onChange={(val) =>
                        handleChange(index, "work_location_district_id", val)
                      }
                      disabled={isDqaApproved || isDqaRejected}
                    />
                    <Err index={index} name="work_location_district_id" />
                  </div>

                  {/* Earnings Last Month */}
                  <div>
                    <ThinTextField
                      label="Earnings Last Month"
                      type="number"
                      value={employment.earnings_last_month || ""}
                      onChange={(e) =>
                        handleChange(
                          index,
                          "earnings_last_month",
                          e.target.value || null,
                        )
                      }
                      disabled={isDqaApproved || isDqaRejected}
                    />
                    <Err index={index} name="earnings_last_month" />
                  </div>

                  {/* Occupation Others Specify */}
                  {employment.occupation_name === "Others" && (
                    <div>
                      <ThinTextField
                        label="Nature of Work Specify"
                        value={employment.occupation_others_specify || ""}
                        onChange={(e) =>
                          handleChange(
                            index,
                            "occupation_others_specify",
                            e.target.value,
                          )
                        }
                        disabled={isDqaApproved || isDqaRejected}
                      />
                      <Err index={index} name="occupation_others_specify" />
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        );
      })}

      {/* Update button */}
      {hasEligibleMembers && !isDqaApproved && !isDqaRejected && (
        <div className="mt-4 flex justify-end">
          <button
            className="bg-teal-600 text-white rounded-lg px-3 py-1.5 hover:bg-teal-700 transition-colors"
            onClick={handleUpdateEmployment}
          >
            Update
          </button>
        </div>
      )}
    </div>
  );
};

export default EmploymentPanel;
